import { useState, useCallback, useRef, useEffect } from "react";

interface UseJobFilePollingProps {
  /**
   * Function to refetch job data
   */
  refetch: () => Promise<{ data?: { uploadedFiles?: unknown[] } }>;

  /**
   * Whether the component/dialog is open and polling should be active
   */
  isOpen: boolean;

  /**
   * Maximum number of polling attempts (default: 5)
   */
  maxAttempts?: number;

  /**
   * Callback when polling successfully completes (files are detected)
   */
  onSuccess?: () => void | Promise<void>;
}

interface UseJobFilePollingReturn {
  /**
   * Start polling for file updates
   * @param expectedFileCount - The expected number of files after upload
   */
  startPolling: (expectedFileCount: number) => void;

  /**
   * Stop polling manually
   */
  stopPolling: () => void;

  /**
   * Whether polling is currently active
   */
  isPolling: boolean;
}

/**
 * Custom hook for polling job file updates after upload
 *
 * Simplified version that provides manual polling control.
 * For consistent polling, consider using react-query's refetchInterval directly.
 *
 * @param props - Configuration for the polling behavior
 * @returns Polling control functions and state
 */
export function useJobFilePolling({
  refetch,
  isOpen,
  maxAttempts = 5,
  onSuccess,
}: UseJobFilePollingProps): UseJobFilePollingReturn {
  const [isPolling, setIsPolling] = useState(false);
  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const attemptCountRef = useRef(0);
  const expectedFileCountRef = useRef(0);
  const isCancelledRef = useRef(false);

  // Start polling function
  const startPolling = useCallback(
    (expectedFileCount: number) => {
      if (!isOpen) return; // Don't start polling if component is closed

      expectedFileCountRef.current = expectedFileCount;
      attemptCountRef.current = 0;
      isCancelledRef.current = false;
      setIsPolling(true);

      const poll = async () => {
        if (isCancelledRef.current || !isOpen) {
          setIsPolling(false);
          return;
        }

        attemptCountRef.current += 1;

        try {
          const result = await refetch();
          const currentFileCount = result.data?.uploadedFiles?.length || 0;

          // Check if expected files are present
          if (currentFileCount >= expectedFileCountRef.current) {
            // Success - stop polling and call success callback
            setIsPolling(false);
            if (onSuccess) {
              await onSuccess();
            }
            return;
          }

          // Check if we should continue polling
          if (
            attemptCountRef.current < maxAttempts &&
            !isCancelledRef.current
          ) {
            // Schedule next poll with exponential backoff
            const delay = Math.min(attemptCountRef.current * 500, 2000);
            pollTimeoutRef.current = setTimeout(poll, delay);
          } else {
            // Max attempts reached or cancelled
            setIsPolling(false);
          }
        } catch {
          // On error, continue polling until max attempts
          if (
            attemptCountRef.current < maxAttempts &&
            !isCancelledRef.current
          ) {
            const delay = Math.min(attemptCountRef.current * 500, 2000);
            pollTimeoutRef.current = setTimeout(poll, delay);
          } else {
            setIsPolling(false);
          }
        }
      };

      // Start first poll after a short delay
      pollTimeoutRef.current = setTimeout(poll, 100);
    },
    [refetch, isOpen, maxAttempts, onSuccess],
  );

  // Stop polling function
  const stopPolling = useCallback(() => {
    isCancelledRef.current = true;
    setIsPolling(false);

    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  // Auto-stop polling when component closes
  useEffect(() => {
    if (!isOpen && isPolling) {
      stopPolling();
    }
  }, [isOpen, isPolling, stopPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    startPolling,
    stopPolling,
    isPolling,
  };
}
